<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper';

const { accountScopedRoute } = useAccount();
const { updateFontSize, currentFontSize } = useFontSize();
const { updateUISettings, uiSettings } = useUISettings();
const { t, locale } = useI18n();
const currentRole = useMapGetter('getCurrentRole');
const isAdministrator = computed(() => currentRole.value === 'administrator');

const themeSelection = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'auto'
);
const zoomValue = ref(parseInt(currentFontSize.value || '16px', 10));

const zoomPercent = computed(
  () => `${Math.round((zoomValue.value / 16) * 100)}%`
);

const languageSelection = computed(
  () => uiSettings.value?.locale || locale.value
);

const languageOptions = computed(() => [
  {
    key: 'es',
    label: t('SETTINGS.PLATFORM_HOME.LANGUAGE.SPANISH'),
  },
  {
    key: 'en',
    label: t('SETTINGS.PLATFORM_HOME.LANGUAGE.ENGLISH'),
  },
]);

const moduleCards = computed(() => [
  {
    label: t('SETTINGS.PLATFORM_HOME.MODULES.AGENTS.LABEL'),
    description: t('SETTINGS.PLATFORM_HOME.MODULES.AGENTS.DESCRIPTION'),
    icon: 'i-lucide-user-round-cog',
    color: 'purple',
    to: accountScopedRoute('agent_list'),
  },
  {
    label: t('SETTINGS.PLATFORM_HOME.MODULES.TEAMS.LABEL'),
    description: t('SETTINGS.PLATFORM_HOME.MODULES.TEAMS.DESCRIPTION'),
    icon: 'i-lucide-users-round',
    color: 'cyan',
    to: accountScopedRoute('settings_teams_list'),
  },
  {
    label: t('SETTINGS.PLATFORM_HOME.MODULES.INBOXES.LABEL'),
    description: t('SETTINGS.PLATFORM_HOME.MODULES.INBOXES.DESCRIPTION'),
    icon: 'i-lucide-inbox',
    color: 'green',
    to: accountScopedRoute('settings_inbox_list'),
  },
  {
    label: t('SETTINGS.PLATFORM_HOME.MODULES.LABELS.LABEL'),
    description: t('SETTINGS.PLATFORM_HOME.MODULES.LABELS.DESCRIPTION'),
    icon: 'i-lucide-tags',
    color: 'pink',
    to: accountScopedRoute('labels_list'),
  },
  {
    label: t('SETTINGS.PLATFORM_HOME.MODULES.ATTRIBUTES.LABEL'),
    description: t('SETTINGS.PLATFORM_HOME.MODULES.ATTRIBUTES.DESCRIPTION'),
    icon: 'i-lucide-code-xml',
    color: 'violet',
    to: accountScopedRoute('attributes_list'),
  },
  {
    label: t('SETTINGS.PLATFORM_HOME.MODULES.AUTOMATION.LABEL'),
    description: t('SETTINGS.PLATFORM_HOME.MODULES.AUTOMATION.DESCRIPTION'),
    icon: 'i-lucide-zap',
    color: 'teal',
    to: accountScopedRoute('automation_list'),
  },
  {
    label: t('SETTINGS.PLATFORM_HOME.MODULES.BOTS.LABEL'),
    description: t('SETTINGS.PLATFORM_HOME.MODULES.BOTS.DESCRIPTION'),
    icon: 'i-lucide-bot',
    color: 'purple',
    to: accountScopedRoute('agent_bots'),
  },
  {
    label: t('SETTINGS.PLATFORM_HOME.MODULES.MACROS.LABEL'),
    description: t('SETTINGS.PLATFORM_HOME.MODULES.MACROS.DESCRIPTION'),
    icon: 'i-lucide-keyboard',
    color: 'pink',
    to: accountScopedRoute('macros_wrapper'),
  },
  {
    label: t('SETTINGS.PLATFORM_HOME.MODULES.CANNED.LABEL'),
    description: t('SETTINGS.PLATFORM_HOME.MODULES.CANNED.DESCRIPTION'),
    icon: 'i-lucide-message-circle',
    color: 'cyan',
    to: accountScopedRoute('canned_list'),
  },
  {
    label: t('SETTINGS.PLATFORM_HOME.MODULES.INTEGRATIONS.LABEL'),
    description: t('SETTINGS.PLATFORM_HOME.MODULES.INTEGRATIONS.DESCRIPTION'),
    icon: 'i-lucide-plug',
    color: 'amber',
    to: accountScopedRoute('settings_applications'),
  },
  {
    label: t('SETTINGS.PLATFORM_HOME.MODULES.AUDIT.LABEL'),
    description: t('SETTINGS.PLATFORM_HOME.MODULES.AUDIT.DESCRIPTION'),
    icon: 'i-lucide-clipboard-list',
    color: 'slate',
    to: accountScopedRoute('auditlogs_list'),
  },
  {
    label: t('SETTINGS.PLATFORM_HOME.MODULES.ROLES.LABEL'),
    description: t('SETTINGS.PLATFORM_HOME.MODULES.ROLES.DESCRIPTION'),
    icon: 'i-lucide-user-check',
    color: 'violet',
    to: accountScopedRoute('custom_roles_list'),
  },
  {
    label: t('SETTINGS.PLATFORM_HOME.MODULES.SLA.LABEL'),
    description: t('SETTINGS.PLATFORM_HOME.MODULES.SLA.DESCRIPTION'),
    icon: 'i-lucide-history',
    color: 'pink',
    to: accountScopedRoute('sla_list'),
  },
  {
    label: t('SETTINGS.PLATFORM_HOME.MODULES.SECURITY.LABEL'),
    description: t('SETTINGS.PLATFORM_HOME.MODULES.SECURITY.DESCRIPTION'),
    icon: 'i-lucide-lock-keyhole',
    color: 'teal',
    to: accountScopedRoute('security_settings_index'),
  },
]);

const setTheme = theme => {
  themeSelection.value = theme;
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, theme);
  setColorTheme(
    window.matchMedia('(prefers-color-scheme: dark)').matches,
    theme
  );
};

const applyZoom = () => {
  updateFontSize(`${zoomValue.value}px`);
};

const updateLanguage = async languageCode => {
  try {
    await updateUISettings({ locale: languageCode });
    locale.value = languageCode;
    useAlert(
      t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_SUCCESS')
    );
  } catch {
    useAlert(
      t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_ERROR')
    );
  }
};

watch(
  currentFontSize,
  value => {
    zoomValue.value = parseInt(value || '16px', 10);
  },
  { immediate: true }
);
</script>

<template>
  <section class="tic-settings-home">
    <header class="tic-settings-home__header">
      <div>
        <h1>{{ t('SETTINGS.PLATFORM_HOME.TITLE') }}</h1>
      </div>
      <p>{{ t('SETTINGS.PLATFORM_HOME.SUBTITLE') }}</p>
    </header>

    <div class="tic-settings-home__top-grid">
      <article class="tic-settings-card tic-settings-card--appearance">
        <h2>
          <span class="i-lucide-palette" />
          {{ t('SETTINGS.PLATFORM_HOME.APPEARANCE.TITLE') }}
        </h2>
        <div class="tic-settings-card__divider" />
        <div class="tic-theme-options">
          <button
            type="button"
            :class="{ 'is-active': themeSelection === 'dark' }"
            @click="setTheme('dark')"
          >
            <span class="i-lucide-moon" />
            <strong>{{ t('SETTINGS.PLATFORM_HOME.APPEARANCE.DARK') }}</strong>
          </button>
          <button
            type="button"
            :class="{ 'is-active': themeSelection === 'light' }"
            @click="setTheme('light')"
          >
            <span class="i-lucide-sun" />
            <strong>{{ t('SETTINGS.PLATFORM_HOME.APPEARANCE.LIGHT') }}</strong>
          </button>
        </div>
      </article>

      <article class="tic-settings-card tic-settings-card--zoom">
        <h2>
          <span class="i-lucide-zoom-in" />
          {{ t('SETTINGS.PLATFORM_HOME.ZOOM.TITLE') }}
        </h2>
        <div class="tic-settings-card__divider" />
        <p>{{ t('SETTINGS.PLATFORM_HOME.ZOOM.DESCRIPTION') }}</p>
        <div class="tic-zoom-control">
          <input
            v-model.number="zoomValue"
            type="range"
            min="14"
            max="20"
            step="1"
            :aria-label="t('SETTINGS.PLATFORM_HOME.ZOOM.ARIA')"
            @change="applyZoom"
          />
          <div class="tic-zoom-control__labels">
            <span>{{ t('SETTINGS.PLATFORM_HOME.ZOOM.SMALL') }}</span>
            <strong>{{ zoomPercent }}</strong>
            <span>{{ t('SETTINGS.PLATFORM_HOME.ZOOM.LARGE') }}</span>
          </div>
        </div>
      </article>

      <article class="tic-settings-card tic-settings-card--language">
        <h2>
          <span class="i-lucide-languages" />
          {{ t('SETTINGS.PLATFORM_HOME.LANGUAGE.TITLE') }}
        </h2>
        <div class="tic-settings-card__divider" />
        <p>{{ t('SETTINGS.PLATFORM_HOME.LANGUAGE.DESCRIPTION') }}</p>
        <div class="tic-language-options">
          <button
            v-for="language in languageOptions"
            :key="language.key"
            type="button"
            :class="{ 'is-active': languageSelection === language.key }"
            @click="updateLanguage(language.key)"
          >
            {{ language.label }}
          </button>
        </div>
      </article>
    </div>

    <article
      v-if="isAdministrator"
      class="tic-settings-card tic-settings-card--modules"
    >
      <h2>
        <span class="i-lucide-sliders-horizontal" />
        {{ t('SETTINGS.PLATFORM_HOME.MODULES_TITLE') }}
      </h2>
      <div class="tic-settings-card__divider" />

      <div class="tic-settings-modules">
        <RouterLink
          v-for="item in moduleCards"
          :key="item.label"
          :to="item.to"
          class="tic-settings-module"
          :class="`tic-settings-module--${item.color}`"
        >
          <span class="tic-settings-module__icon" :class="item.icon" />
          <span>
            <strong>{{ item.label }}</strong>
            <small>{{ item.description }}</small>
          </span>
        </RouterLink>
      </div>
    </article>
  </section>
</template>

<style scoped lang="scss">
.tic-settings-home {
  --tic-panel: rgba(9, 16, 31, 0.72);
  --tic-panel-strong: rgba(10, 16, 36, 0.86);
  --tic-border: rgba(148, 234, 255, 0.16);
  --tic-text: #f8fafc;
  --tic-muted: rgba(191, 219, 254, 0.82);

  width: 100%;
  min-height: calc(100vh - 7rem);
  padding: 0 1.75rem 8rem;
  color: var(--tic-text);
}

.tic-settings-home__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  margin: 0.3rem 0 1.6rem;

  h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 800;
    line-height: 1.1;
  }

  p {
    margin: 0.35rem 0 0;
    color: var(--tic-muted);
    font-weight: 600;
    text-align: right;
  }
}

.tic-settings-home__top-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1.5rem;
  margin-bottom: 1.75rem;
}

.tic-settings-card {
  border: 1px solid var(--tic-border);
  border-radius: 1.35rem;
  background: linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.08),
      transparent 46%
    ),
    var(--tic-panel);
  box-shadow:
    0 1.2rem 3.4rem rgba(0, 0, 0, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  padding: 1.5rem;

  h2 {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    font-size: 1rem;
    font-weight: 800;
  }

  p {
    color: var(--tic-muted);
    margin: 0 0 1.8rem;
  }
}

.tic-settings-card--language {
  grid-column: 1 / -1;
}

.tic-settings-card__divider {
  height: 1px;
  margin: 1rem 0;
  background: rgba(148, 234, 255, 0.12);
}

.tic-theme-options,
.tic-language-options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.8rem;
    min-height: 3.75rem;
    border: 1px solid rgba(148, 234, 255, 0.14);
    border-radius: 0.85rem;
    background: rgba(255, 255, 255, 0.035);
    color: var(--tic-muted);
    cursor: pointer;
    transition:
      border-color 0.18s ease,
      background 0.18s ease,
      color 0.18s ease;

    span {
      font-size: 1.1rem;
    }

    &.is-active {
      border-color: rgba(38, 217, 247, 0.95);
      background: rgba(38, 217, 247, 0.1);
      color: var(--tic-text);
      box-shadow: inset 0 0 0 1px rgba(38, 217, 247, 0.28);
    }
  }
}

.tic-theme-options button {
  flex-direction: column;
  min-height: 6rem;
}

.tic-zoom-control {
  margin-top: 1.8rem;

  input {
    width: 100%;
    accent-color: #26d9f7;
  }
}

.tic-zoom-control__labels {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-top: 0.8rem;
  color: var(--tic-muted);
  font-size: 0.8rem;
  font-weight: 700;

  strong {
    color: #26d9f7;
    font-size: 1rem;
  }

  span:last-child {
    text-align: right;
  }
}

.tic-settings-card--modules {
  margin-bottom: 6rem;
  padding: 1.5rem;
}

.tic-settings-modules {
  display: grid;
  grid-template-columns: repeat(6, minmax(10rem, 1fr));
  gap: 1rem;
}

.tic-settings-module {
  display: flex;
  align-items: center;
  gap: 1rem;
  min-height: 6.25rem;
  padding: 1rem;
  border: 1px solid rgba(148, 234, 255, 0.14);
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.035);
  color: var(--tic-text);
  text-decoration: none;
  transition:
    transform 0.18s ease,
    border-color 0.18s ease,
    background 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(38, 217, 247, 0.42);
    background: rgba(38, 217, 247, 0.08);
  }

  strong,
  small {
    display: block;
  }

  strong {
    margin-bottom: 0.25rem;
    font-size: 0.9rem;
    font-weight: 800;
  }

  small {
    color: var(--tic-muted);
    font-size: 0.72rem;
    line-height: 1.25;
  }
}

.tic-settings-module__icon {
  display: grid;
  place-items: center;
  flex: 0 0 2.55rem;
  width: 2.55rem;
  height: 2.55rem;
  border-radius: 0.7rem;
  font-size: 1.15rem;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
  opacity: 1;
}

.tic-settings-module--purple .tic-settings-module__icon {
  background: rgba(139, 42, 201, 0.42);
  color: #f0dcff;
}
.tic-settings-module--cyan .tic-settings-module__icon {
  background: rgba(38, 217, 247, 0.34);
  color: #b6f6ff;
}
.tic-settings-module--green .tic-settings-module__icon,
.tic-settings-module--teal .tic-settings-module__icon {
  background: rgba(47, 255, 210, 0.28);
  color: #99f6e4;
}
.tic-settings-module--pink .tic-settings-module__icon {
  background: rgba(255, 77, 141, 0.32);
  color: #f9a8d4;
}
.tic-settings-module--violet .tic-settings-module__icon {
  background: rgba(168, 85, 247, 0.36);
  color: #e9d5ff;
}
.tic-settings-module--amber .tic-settings-module__icon {
  background: rgba(245, 158, 11, 0.3);
  color: #fde68a;
}
.tic-settings-module--slate .tic-settings-module__icon {
  background: rgba(148, 163, 184, 0.28);
  color: #e2e8f0;
}

:global(body:not(.dark)) .tic-settings-home {
  --tic-panel: rgba(255, 255, 255, 0.92);
  --tic-panel-strong: rgba(255, 255, 255, 0.98);
  --tic-border: rgba(255, 255, 255, 0.92);
  --tic-text: #111827;
  --tic-muted: #53657f;
}

:global(body:not(.dark)) .tic-settings-card {
  background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.96),
      rgba(246, 253, 255, 0.9)
    ),
    #ffffff;
  box-shadow: 0 12px 32px rgba(31, 38, 135, 0.08);
}

:global(body:not(.dark)) .tic-settings-module,
:global(body:not(.dark)) .tic-theme-options button,
:global(body:not(.dark)) .tic-language-options button {
  background: rgba(255, 255, 255, 0.82);
  border-color: rgba(15, 23, 42, 0.08);
  box-shadow: none;
}

:global(body:not(.dark)) .tic-settings-module__icon {
  filter: saturate(1.35);
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.04);
}

:global(body:not(.dark))
  .tic-settings-module--purple
  .tic-settings-module__icon {
  background: rgba(139, 42, 201, 0.18);
  color: #7e22ce;
}

:global(body:not(.dark)) .tic-settings-module--cyan .tic-settings-module__icon {
  background: rgba(38, 217, 247, 0.18);
  color: #0284a8;
}

:global(body:not(.dark)) .tic-settings-module--green .tic-settings-module__icon,
:global(body:not(.dark)) .tic-settings-module--teal .tic-settings-module__icon {
  background: rgba(20, 184, 166, 0.18);
  color: #0f766e;
}

:global(body:not(.dark)) .tic-settings-module--pink .tic-settings-module__icon {
  background: rgba(236, 72, 153, 0.16);
  color: #be185d;
}

:global(body:not(.dark))
  .tic-settings-module--violet
  .tic-settings-module__icon {
  background: rgba(168, 85, 247, 0.16);
  color: #7e22ce;
}

:global(body:not(.dark))
  .tic-settings-module--amber
  .tic-settings-module__icon {
  background: rgba(245, 158, 11, 0.18);
  color: #b45309;
}

:global(body:not(.dark))
  .tic-settings-module--slate
  .tic-settings-module__icon {
  background: rgba(71, 85, 105, 0.14);
  color: #334155;
}

:global(body:not(.dark)) .tic-theme-options button.is-active,
:global(body:not(.dark)) .tic-language-options button.is-active {
  background: linear-gradient(
    135deg,
    rgba(139, 42, 201, 0.12),
    rgba(38, 217, 247, 0.12)
  );
}

:global(body:not(.dark)) .tic-settings-card__divider {
  background: rgba(15, 23, 42, 0.08);
}

@media (max-width: 1280px) {
  .tic-settings-modules {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 860px) {
  .tic-settings-home {
    padding: 0 1rem 1rem;
  }

  .tic-settings-home__header,
  .tic-settings-home__top-grid {
    grid-template-columns: 1fr;
    flex-direction: column;
  }

  .tic-settings-home__header p {
    text-align: left;
  }

  .tic-settings-modules,
  .tic-theme-options,
  .tic-language-options {
    grid-template-columns: 1fr;
  }
}
</style>
